import axios from "axios";
import { IndexValue, PeerAverageSeries, PeerSource, PeersList } from "@/models/stock-peers";
import { ListQueryParams } from "@/models/table";

/**
 * A stock's peers for the peers grid, from the market-first peers API (ortex/ted/ted#1353).
 *
 * Addressed by trading item id only: the DRF peers endpoint does not take a stock id. Callers pass
 * `data.tradingitem_id`; the grid's own `universe_id` (a stock id) is deliberately ignored.
 * `data.source` forces "market" or "gics"; omitted, the API serves market peers where the stock has
 * them and GICS peers otherwise, and says which in `meta`.
 */
export const getStockPeers = async (params: ListQueryParams): Promise<PeersList> => {
    const { page_size, page, filter_options, signal, data, next_page_url } = params;
    const tradingitemId = data?.tradingitem_id;
    const source: PeerSource | undefined = data?.source;

    const url = next_page_url || `/interface/api/peers/tradingitem/${tradingitemId}/list`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options, ...(source ? { source } : {}) };

    const res = await axios.get<PeersList>(url, { params: defaultParams, signal });
    return res.data;
};

/** The equal-weighted business-peer average price line, for the peers chart. */
export const getPeerAverageSeries = async (tradingitemId: number): Promise<PeerAverageSeries> => {
    const res = await axios.get<PeerAverageSeries>(`/interface/api/peers/tradingitem/${tradingitemId}/average`, { params: { source: "business" } });
    return res.data;
};

export const getIndexValue = async (detail: string) => {
    const res = await axios.get<IndexValue>("/API/ts/index/", { params: { detail } });
    return res.data;
};

export const getPeersNote = async (companyId: number) => {
    const res = await axios.get<{ note: string; obfuscated?: boolean }>(`/interface/api/commentary/company/${companyId}/peers/note/`);
    return res.data;
};
